import React from 'react';
import { Lock, Eye, Shield, Database, Mail, Server, Key, FileCheck, UserCheck } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center mb-8">
            <div className="bg-cyan-100 p-4 rounded-xl mr-4">
              <Lock className="w-8 h-8 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-600 mt-2">Last updated: January 11, 2025</p>
            </div>
          </div>

          <div className="mb-8 p-6 bg-cyan-50 rounded-xl border-l-4 border-cyan-600">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Our Commitment</h3>
            <p className="text-gray-700 leading-relaxed">
              Fluxara is committed to protecting your personal information and your right to privacy. We use advanced AI technology to generate T-shirt designs while maintaining the highest standards of data security and user privacy.
            </p>
          </div>

          <div className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Database className="w-6 h-6 mr-2 text-cyan-600" />
                Information We Collect
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">Account Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Name and email address for account creation</li>
                <li>Profile information you choose to provide</li>
                <li>Account preferences and settings</li>
                <li>Display name or username</li>
                <li>Password (encrypted)</li>
                <li>Profile information (if provided via Google OAuth)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Usage Data</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>AI generation requests and preferences</li>
                <li>Design prompts you submit</li>
                <li>Generated images and designs</li>
                <li>Project names and metadata</li>
                <li>Credit usage and purchase history</li>
                <li>Feature usage analytics (anonymized)</li>
                <li>Technical information (IP address, browser type, device info)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Content Data</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Text prompts and generation parameters</li>
                <li>Generated AI content (images)</li>
                <li>Project files and metadata</li>
                <li>Version history and edits</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Eye className="w-6 h-6 mr-2 text-cyan-600" />
                How We Use Your Information
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">Service Provision</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Process AI generation requests</li>
                <li>Manage your account and credits</li>
                <li>Provide customer support</li>
                <li>Send service-related communications</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Service Improvement</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Analyze usage patterns (anonymized)</li>
                <li>Improve AI model performance</li>
                <li>Develop new features</li>
                <li>Ensure platform security</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-2 text-cyan-600" />
                Data Security & Protection
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Server className="w-5 h-5 text-cyan-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Technical Safeguards</h4>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1 ml-7">
                    <li>• End-to-end encryption for data transmission</li>
                    <li>• Encrypted data storage with AES-256</li>
                    <li>• Secure cloud infrastructure (Firebase/GCP)</li>
                    <li>• Regular security audits and monitoring</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Key className="w-5 h-5 text-cyan-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Access Controls</h4>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1 ml-7">
                    <li>• Multi-factor authentication support</li>
                    <li>• Role-based access restrictions</li>
                    <li>• Regular access reviews</li>
                    <li>• Secure API authentication</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <UserCheck className="w-5 h-5 text-cyan-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Operational Security</h4>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1 ml-7">
                    <li>• Limited employee access to user data</li>
                    <li>• Confidentiality agreements</li>
                    <li>• Security incident response procedures</li>
                    <li>• Regular security training</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <FileCheck className="w-5 h-5 text-cyan-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Data Minimization</h4>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1 ml-7">
                    <li>• Collect only necessary information</li>
                    <li>• Automatic data retention limits</li>
                    <li>• Secure data deletion procedures</li>
                    <li>• Anonymous analytics where possible</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Security</h2>
              
              <div className="bg-green-50 p-6 rounded-xl border-l-4 border-green-500 mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2">PCI DSS Compliance</h3>
                <p className="text-gray-700 leading-relaxed">
                  We never store, process, or have access to your payment card information. All payment processing is handled by our certified payment partners using industry-standard PCI DSS compliance.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Payment Protection</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Hosted payment fields technology</li>
                    <li>• No card data stored on our servers</li>
                    <li>• Encrypted payment transmission</li>
                    <li>• Fraud detection and prevention</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Transaction Security</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Secure payment gateway integration</li>
                    <li>• Transaction monitoring</li>
                    <li>• Refund and dispute handling</li>
                    <li>• Receipt and billing transparency</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">AI Processing & Content</h2>
              
              <div className="bg-cyan-50 p-6 rounded-xl mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Content Processing</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Your design prompts and generated content are processed using advanced AI models to create T-shirt designs. Here's how we handle your content:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Prompts are processed securely in isolated environments</li>
                  <li>Generated content belongs to you (see our License terms)</li>
                  <li>No content is used to train AI models without consent</li>
                  <li>Content is automatically deleted after specified retention periods</li>
                  <li>You can delete your content at any time</li>
                </ul>
              </div>

              <div className="bg-turquoise-50 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">AI Model Integrity</h3>
                <p className="text-gray-700 leading-relaxed">
                  We ensure our AI models operate fairly and securely, without bias or inappropriate content generation, while maintaining the highest quality standards.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the following third-party services to operate our platform:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Firebase (Google)</h4>
                  <p className="text-gray-700 text-sm">Authentication, database, storage, and hosting services.</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">OpenAI</h4>
                  <p className="text-gray-700 text-sm">AI language model for prompt enhancement.</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Ideogram API</h4>
                  <p className="text-gray-700 text-sm">AI image generation service.</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">PhotoRoom API</h4>
                  <p className="text-gray-700 text-sm">Background removal service.</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">PayPlus</h4>
                  <p className="text-gray-700 text-sm">Payment processing for subscriptions.</p>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mt-4">
                These services have their own privacy policies. We encourage you to review them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Privacy Rights</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-cyan-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Access & Control</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Access your personal data</li>
                    <li>• Update or correct information</li>
                    <li>• Download your data</li>
                    <li>• Delete your account and data</li>
                  </ul>
                </div>

                <div className="bg-turquoise-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Privacy Choices</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Opt-out of marketing communications</li>
                    <li>• Control data sharing preferences</li>
                    <li>• Manage cookie settings</li>
                    <li>• Request data portability</li>
                  </ul>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us at support@fluxaraai.com or use the account deletion feature in your Profile settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Account Data</h4>
                  <p className="text-gray-700 text-sm">
                    Retained while your account is active, plus 90 days after deletion for security purposes.
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Generated Content</h4>
                  <p className="text-gray-700 text-sm">
                    Stored as long as you keep it in your projects. Automatically deleted 1 year after account closure.
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Analytics Data</h4>
                  <p className="text-gray-700 text-sm">
                    Anonymized usage data retained for up to 2 years for service improvement purposes.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Maintain your login session</li>
                <li>Remember your preferences</li>
                <li>Analyze usage patterns via Firebase Analytics</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookies through your browser settings, but this may affect Service functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Service is not intended for users under 13 years of age. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal data, please contact us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. We will notify you of significant changes by:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Posting the new Privacy Policy on this page</li>
                <li>Updating the "Last updated" date</li>
                <li>Sending an email notification for material changes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Mail className="w-6 h-6 mr-2 text-cyan-600" />
                Questions About Privacy?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We're here to help you understand how we protect your privacy and data.
              </p>
              <div className="bg-gradient-to-br from-cyan-50 to-turquoise-50 p-6 rounded-xl border border-cyan-200">
                <p className="text-gray-700 mb-2"><strong>Email:</strong> support@fluxaraai.com</p>
                <p className="text-gray-700 mb-2"><strong>Support:</strong> support@fluxaraai.com</p>
                <p className="text-gray-700 mb-2"><strong>Data Protection Officer:</strong> support@fluxaraai.com</p>
                <p className="text-gray-700"><strong>Address:</strong> Fluxara Inc., 123 Design Street, San Francisco, CA 94102</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-gray-100 rounded-lg text-center">
              <p className="text-sm text-gray-600">
                This Privacy Policy is effective as of the date listed above and will remain in effect except with respect to any changes in its provisions in the future.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;